
import SlideTransition from './transitions/SlideTransition.vue';
import { restApi } from '@/components/mixins/restApi';
import { showMessage } from '@/components/mixins/showMessage';
import mixins from 'vue-typed-mixins';
import { mapGetters } from 'vuex';
import moment from 'moment';
import { PUBLISH_VERSION_MODAL_KEY } from '@/constants';
import AlertNotificationModal from '@/components/AlertNotificationModal.vue';
import ModalButtonSuccess from '@/components/ModalButtonSuccess.vue';
import ModalButtonCancel from '@/components/ModalButtonCancel.vue';
import { N8nSpinner } from 'n8n-design-system';

interface Item {
	id: number;
	nome?: string;
	nomePublicacao?: string;
	descricaoPublicacao?: string;
	modificadoPorUsuario: {
		nome?: string;
	};
	modificadoEmDataHora: string;
	publicado?: boolean;
}

interface GroupedItem {
	type: 'version' | 'alteracoes';
	version?: Item;
	alteracoes?: Item[];
	id: number;
}

export default mixins(restApi, showMessage).extend({
	name: 'VersionHistory',
	components: {
		SlideTransition,
		AlertNotificationModal,
		ModalButtonSuccess,
		ModalButtonCancel,
		N8nSpinner,
	},
	data() {
		return {
			versionData: [] as Item[],
			openAlteracoes: {} as Record<number, boolean>,
			activeVersionId: null as number | null,
			showRestoreAlert: false,
			versionToRestore: null as Item | null,
			isLoadingVersions: false,
			showPublishAlert: false,
			versionToRepublish: null as Item | null,
			isPublishingVersion: false,
		};
	},
	computed: {
		...mapGetters(['workspace']),
		groupedItems(): GroupedItem[] {
			// Ordenar todos os itens do mais recente ao mais antigo
			const sortedData = [...this.versionData].sort((a, b) => {
				return moment(b.modificadoEmDataHora).diff(moment(a.modificadoEmDataHora));
			});

			const result: GroupedItem[] = [];
			let currentAlteracoes: Item[] = [];
			let groupId = 1;

			sortedData.forEach((item, index) => {
				if (item.nomePublicacao) {
					// Se houver alterações acumuladas, adicionar o grupo antes da versão
					if (currentAlteracoes.length > 0) {
						result.push({
							type: 'alteracoes',
							alteracoes: currentAlteracoes,
							id: groupId++,
						});
						currentAlteracoes = [];
					}
					
					// Adicionar a versão publicada
					result.push({
						type: 'version',
						version: item,
						id: item.id,
					});
				} else {
					// Acumular alterações não publicadas
					currentAlteracoes.push(item);
					
					// Se for o último item e houver alterações acumuladas, adicionar o grupo
					if (index === sortedData.length - 1 && currentAlteracoes.length > 0) {
						result.push({
							type: 'alteracoes',
							alteracoes: currentAlteracoes,
							id: groupId++,
						});
					}
				}
			});

			return result;
		},
	workflowId(): string {
		return this.$route.params.name || this.$route.params.workflowid || this.$store.getters.workflowId || '' ;
	},
},
	async mounted() {
		this.$root.$on('workflowSaved', this.handleWorkflowSaved);
		await this.loadWorkflowVersions();
	},
	beforeDestroy() {
		this.$root.$off('workflowSaved', this.handleWorkflowSaved);
	},
	methods: {
		handleWorkflowSaved(payload: { workflowId?: string | number } = {}) {
			if (!this.workflowId) {
				return;
			}

			if (payload.workflowId && String(payload.workflowId) !== String(this.workflowId)) {
				return;
			}

			this.loadWorkflowVersions();
		},
		async loadWorkflowVersions() {
			this.isLoadingVersions = true;
			try {
				const response = await this.restApi().getWorkflowVersion(this.workspace.id, this.workflowId);
				this.versionData = response || [];
				this.activeVersionId = this.$route.params.versionid;
				
				// Setar informações da versão ativa no store
				if (this.activeVersionId && this.versionData.length > 0) {
					const activeVersion = this.versionData.find((v: Item) => v.id === this.activeVersionId);
					if (activeVersion) {
						this.$store.commit('setVersionInfo', {
							nomePublicacao: activeVersion.nomePublicacao || null,
							descricaoPublicacao: activeVersion.descricaoPublicacao || null,
							modificadoEmDataHora: activeVersion.modificadoEmDataHora || null,
						});
					}
				}
			} catch (error: any) {
				console.error('Erro ao carregar versões do workflow:', error);
				this.$store.commit('activeAlert', {
					message: error?.notifications?.[0],
					status: 'error',
				});
			}
			finally {
				this.isLoadingVersions = false;
			}
		},
		handleVersionClick(item: Item) {
			if (!item) return;
			
			// Atualizar versão ativa
			this.activeVersionId = item.id;
			
			// Setar informações da versão no store
			this.$store.commit('setVersionInfo', {
				nomePublicacao: item.nomePublicacao || null,
				descricaoPublicacao: item.descricaoPublicacao || null,
				modificadoEmDataHora: item.modificadoEmDataHora || null,
			});
			
			// Emitir evento para carregar a versão
			this.$emit('loadVersion', { versionId: item.id, workflowId: this.workflowId });
		},
		toggleAlteracoes(groupId: number) {
			this.$set(this.openAlteracoes, groupId, !this.openAlteracoes[groupId]);
		},
		isAlteracoesOpen(groupId: number) {
			return this.openAlteracoes[groupId] === true;
		},
		async openVersionForEdit(item: Item) {
			try {
				// Navegar para a rota de edição de versão
				this.$router.push({
					name: 'EditVersionById',
					params: {
						name: this.workflowId,
						versionid: item.id.toString(), 
						workflowid: this.workflowId,
					},
				});
				
			} catch (error: any) {
				console.error('Erro ao carregar versão para edição:', error);
				this.$store.commit('activeAlert', {
					message: 'Erro ao carregar versão: ' + error?.notifications,
					status: 'error',
				});
			}
		},
		requestOpenVersionForEdit(item: Item) {
			this.versionToRestore = item;
			this.showRestoreAlert = true;
		},
		async confirmOpenVersionForEdit() {
			if (!this.versionToRestore) {
				this.showRestoreAlert = false;
				return;
			}

			const itemToRestore = this.versionToRestore;
			this.showRestoreAlert = false;
			this.versionToRestore = null;

			await this.openVersionForEdit(itemToRestore);
		},
		cancelOpenVersionForEdit() {
			this.showRestoreAlert = false;
			this.versionToRestore = null;
		},
		requestPublishVersion(item: Item) {
			this.versionToRepublish = item;
			this.showPublishAlert = true;
		},
		async confirmPublishVersion() {
			if (!this.versionToRepublish || !this.versionToRepublish.nomePublicacao) {
				this.cancelPublishVersion();
				return;
			}

			const versionToRepublish = this.versionToRepublish;
			this.isPublishingVersion = true;

			try {
				await this.restApi().publishWorkflowVersion(
					this.workspace.id,
					this.workflowId,
					String(versionToRepublish.id),
					{
						nomeVersao: versionToRepublish.nomePublicacao,
						descricaoVersao: versionToRepublish.descricaoPublicacao || '',
					},
				);

				this.$store.commit('activeAlert', {
					message: 'Versão publicada com sucesso',
				});

				// await this.loadWorkflowVersions();

				const targetRoute = this.$router.resolve({
                    name: 'NodeViewExisting',
					params: { name: this.workflowId, mode: 'Editar' },
                });
				const currentRoute = this.$route;
				const isSameRoute =
					currentRoute.name === 'NodeViewExisting' &&
						String(currentRoute.params.name) === String(this.workflowId);

				if (isSameRoute) {
					window.location.reload();
				} else {
					this.$router.push(targetRoute.location);
				}
			} catch (error: any) {
				console.error('Erro ao publicar versão:', error);
				this.$store.commit('activeAlert', {
					message: 'Erro ao publicar versão: ' + error?.notifications,
					status: 'error',
				});
			} finally {
				this.isPublishingVersion = false;
				this.cancelPublishVersion();
			}
		},
		cancelPublishVersion() {
			this.showPublishAlert = false;
			this.versionToRepublish = null;
		},
		handleMenuAction(command: { action: string; item: Item }) {
			const { action, item } = command;
			
			switch (action) {
				case 'edit':
					this.requestOpenVersionForEdit(item);
					break;
				case 'publish':
					if (item.nomePublicacao) {
						this.requestPublishVersion(item);
						break;
					}

					this.$store.commit('setVersionToPublish', item.id);
					this.$store.commit('setWorkflowIdToPublish', this.workflowId || null);
					this.$store.dispatch('ui/openModalWithData', {
						modalKey: PUBLISH_VERSION_MODAL_KEY,
						data: { skipRedirectAfterPublish: false },
					});
					break;
				case 'openNewTab':
					// Abrir em nova aba
					const url = this.$router.resolve({
						name: 'VersionById',
						params: { versionid: item.id, workflowid: this.workflowId },
					}).href;
					window.open(url, '_blank');
					break;
				case 'saveCopy':
					this.$emit('saveCopy', item);
					break;
			}
		},
		formatDate(date: string) {
			if (!date) return '';
			const dateUtc = moment.utc(date);
			const localDate = moment(dateUtc).local().format('DD/MM/YYYY [às] HH[h]:mm[min]');
			return localDate;
		},
	},
});
